import { Carousel, CarouselSlide } from '@vp/swan';
import { SupportedLocale, __ } from '@99designs/i18n';
import { usePricingProviderContext } from '@99designs/pricing';
import { NewDesignProductPresenter } from '../../lib/presenter/productsPresenter';
import { getPriceForProduct } from '../../lib/pricing';
import { ProductTile } from '../ProductTile';
import type { NavigationTracking } from '@99designs/tracking';
import type { DesignProduct, DesignProductPresenter } from '../../lib/presenter/types';

interface ProductCarouselProps {
    products: DesignProduct[];
    locale: SupportedLocale;
    productClickedTrackingData?: (
        product: DesignProductPresenter,
        index: number
    ) => NavigationTracking;
    maxSlides?: number;
    SeeAllTile?: React.ReactNode;
}

export function ProductCarousel({
    products,
    locale,
    productClickedTrackingData,
    maxSlides = 4,
    SeeAllTile,
}: ProductCarouselProps) {
    const { productPrices, loading: priceLoading } = usePricingProviderContext();

    return (
        <Carousel
            data-testid="product-carousel"
            slidesToShow={{ xs: 2, sm: 4, lg: maxSlides }}
            slidesToScroll={2}
            infinite={false}
            gridGutters
            accessibleTextForPrevious={__('Go to previous slide')}
            accessibleTextForNext={__('Go to next slide')}
            arrowVerticalOffset="-17px"
        >
            {products.map((product, index) => {
                const presenter = NewDesignProductPresenter(product);
                const productPrice = getPriceForProduct(product, productPrices);

                return (
                    <CarouselSlide
                        key={presenter.mpvId}
                        data-testid="product-carousel-slide"
                        data-product-id={presenter.mpvId}
                    >
                        <ProductTile
                            key={presenter.mpvId}
                            title={presenter.name}
                            imageSrc={
                                presenter.previewImage({ cloudinaryOptions: { width: 450 } }).url
                            }
                            imageAspectRatio={
                                presenter.previewImage({ cloudinaryOptions: { width: 450 } })
                                    .aspectRatio
                            }
                            ctaLink={presenter.ctaUrl(locale)}
                            trackingData={
                                productClickedTrackingData
                                    ? productClickedTrackingData(presenter, index)
                                    : undefined
                            }
                            price={productPrice}
                            priceLoading={priceLoading}
                            testId={`product-carousel-tile-${presenter.mpvId}`}
                        />
                    </CarouselSlide>
                );
            })}
            {SeeAllTile && (
                <CarouselSlide
                    key="see-all"
                    data-testid="product-carousel-slide"
                    data-product-id="see-all"
                >
                    {SeeAllTile}
                </CarouselSlide>
            )}
        </Carousel>
    );
}
